import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { motion } from "framer-motion";
import api, { formatApiErrorDetail } from "@/lib/api";
import { PageHeader, Section } from "@/components/layout/Section";
import { IMAGES } from "@/constants/branding";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  ArrowLeft, MapPin, Clock, Calendar, Ticket, Share2, Facebook, Twitter, MessageCircle, Copy, Mail,
} from "lucide-react";

export default function EventDetails() {
  const { id } = useParams();
  const nav = useNavigate();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api
      .get("/events")
      .then((r) => {
        const found = (r.data || []).find((e) => e.id === id);
        if (!found) {
          toast.error("Event not found.");
          nav("/events", { replace: true });
          return;
        }
        setEvent(found);
      })
      .finally(() => setLoading(false));
  }, [id, nav]);

  if (loading) return <div className="min-h-[60vh] flex items-center justify-center text-slate-500">Loading…</div>;
  if (!event) return null;

  const d = new Date(event.starts_at);
  const dateLong = d.toLocaleString("en", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
  const time = d.toLocaleString("en", { hour: "numeric", minute: "2-digit" });

  const shareUrl = typeof window !== "undefined" ? window.location.href : "";
  const shareText = `Join us at ${event.title} — ${dateLong} at ${event.location || "Christ Love Breed Church"}. #CLBC`;

  const share = (channel) => {
    const enc = encodeURIComponent;
    const links = {
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${enc(shareUrl)}`,
      twitter: `https://twitter.com/intent/tweet?text=${enc(shareText)}&url=${enc(shareUrl)}`,
      whatsapp: `https://wa.me/?text=${enc(shareText + " " + shareUrl)}`,
      email: `mailto:?subject=${enc(event.title)}&body=${enc(shareText + "\n\n" + shareUrl)}`,
    };
    if (channel === "copy") {
      navigator.clipboard.writeText(shareUrl);
      toast.success("Link copied!");
      return;
    }
    window.open(links[channel], "_blank", "noopener,noreferrer");
  };

  const register = async (e) => {
    e.preventDefault();
    setBusy(true);
    const fd = new FormData(e.target);
    try {
      await api.post(`/events/${event.id}/register`, {
        name: fd.get("name"),
        email: fd.get("email"),
        phone: fd.get("phone") || null,
      });
      toast.success("You're registered! We'll be in touch.");
      e.target.reset();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not register.");
    } finally {
      setBusy(false);
    }
  };

  const poster = event.image || IMAGES.worship;

  return (
    <div data-testid="event-details-page">
      <div className="radial-bg pt-10">
        <div className="container-x">
          <Link to="/events" className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-royal">
            <ArrowLeft className="w-4 h-4" /> All events
          </Link>
        </div>
      </div>

      <Section className="pt-10">
        <div className="grid lg:grid-cols-12 gap-14">
          {/* Poster */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-6"
          >
            <div className="relative rounded-[2.5rem] overflow-hidden shadow-2xl border-4 border-white aspect-[4/5]">
              <img src={poster} alt={event.title} className="w-full h-full object-cover" />
              <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-royal-800/85 via-royal-800/40 to-transparent p-8">
                <div className="text-center bg-white/95 text-royal-800 rounded-2xl px-6 py-4 shadow-xl inline-block">
                  <div className="text-[10px] uppercase tracking-widest text-gold-500 font-semibold">
                    {d.toLocaleString("en", { month: "short" })}
                  </div>
                  <div className="font-serif text-4xl leading-none mt-1">{d.getDate()}</div>
                  <div className="text-[10px] text-slate-500 mt-1">{d.toLocaleString("en", { year: "numeric" })}</div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Details */}
          <div className="lg:col-span-6">
            <div className="kicker mb-4">CLBC Event</div>
            <h1 className="font-serif text-4xl sm:text-5xl text-royal-800 leading-tight tracking-tight">
              {event.title}
            </h1>

            <div className="mt-8 grid grid-cols-2 gap-4">
              <div className="card-soft p-5">
                <div className="w-10 h-10 rounded-full bg-gold/15 text-gold-500 flex items-center justify-center">
                  <Calendar className="w-4 h-4" />
                </div>
                <div className="mt-3 text-xs uppercase tracking-widest text-slate-500">Date</div>
                <div className="font-serif text-lg text-royal">{dateLong}</div>
              </div>
              <div className="card-soft p-5">
                <div className="w-10 h-10 rounded-full bg-royal/10 text-royal flex items-center justify-center">
                  <Clock className="w-4 h-4" />
                </div>
                <div className="mt-3 text-xs uppercase tracking-widest text-slate-500">Time</div>
                <div className="font-serif text-lg text-royal">{time}</div>
              </div>
              {event.location && (
                <div className="card-soft p-5 col-span-2">
                  <div className="w-10 h-10 rounded-full bg-gold/15 text-gold-500 flex items-center justify-center">
                    <MapPin className="w-4 h-4" />
                  </div>
                  <div className="mt-3 text-xs uppercase tracking-widest text-slate-500">Venue</div>
                  <div className="font-serif text-lg text-royal">{event.location}</div>
                </div>
              )}
            </div>

            {event.description && (
              <p className="mt-8 text-slate-600 leading-relaxed whitespace-pre-line">{event.description}</p>
            )}

            <div className="mt-10 flex flex-wrap gap-3">
              {event.registration_required && (
                <Dialog>
                  <DialogTrigger asChild>
                    <button className="btn-primary" data-testid="event-details-register">
                      <Ticket className="w-4 h-4" /> Register to attend
                    </button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle className="font-serif text-2xl text-royal">Register for {event.title}</DialogTitle>
                      <DialogDescription>{dateLong} · {time}</DialogDescription>
                    </DialogHeader>
                    <form onSubmit={register} className="space-y-4">
                      <div>
                        <Label>Full name</Label>
                        <Input name="name" required data-testid="event-reg-name" />
                      </div>
                      <div>
                        <Label>Email</Label>
                        <Input name="email" type="email" required data-testid="event-reg-email" />
                      </div>
                      <div>
                        <Label>Phone (optional)</Label>
                        <Input name="phone" data-testid="event-reg-phone" />
                      </div>
                      <DialogFooter>
                        <button disabled={busy} className="btn-primary" data-testid="event-reg-submit">
                          Confirm Registration
                        </button>
                      </DialogFooter>
                    </form>
                  </DialogContent>
                </Dialog>
              )}
              <button
                type="button"
                onClick={() => share("copy")}
                className="btn-secondary"
                data-testid="event-share-copy"
              >
                <Share2 className="w-4 h-4" /> Share
              </button>
            </div>

            {/* Share buttons */}
            <div className="mt-6 flex flex-wrap items-center gap-3">
              <span className="text-xs uppercase tracking-widest text-slate-500">Spread the word:</span>
              <ShareBtn onClick={() => share("facebook")} label="Facebook" icon={Facebook} testId="event-share-facebook" />
              <ShareBtn onClick={() => share("twitter")} label="X / Twitter" icon={Twitter} testId="event-share-twitter" />
              <ShareBtn onClick={() => share("whatsapp")} label="WhatsApp" icon={MessageCircle} testId="event-share-whatsapp" />
              <ShareBtn onClick={() => share("email")} label="Email" icon={Mail} testId="event-share-email" />
              <ShareBtn onClick={() => share("copy")} label="Copy link" icon={Copy} testId="event-share-link" />
            </div>
          </div>
        </div>
      </Section>
    </div>
  );
}

function ShareBtn({ onClick, label, icon: Icon, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      title={label}
      aria-label={label}
      className="w-10 h-10 rounded-full border border-slate-200 text-slate-600 hover:text-royal hover:border-gold hover:bg-gold/5 flex items-center justify-center transition-colors"
    >
      <Icon className="w-4 h-4" />
    </button>
  );
}
